import { useState, useEffect, useRef, useCallback, useContext } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import GlobalContext from '../context/GlobalContext.js'
import getTenantTaskInstance from '../functions/API/getTenantTaskInstance.js'
import postUpdateFieldValue from '../functions/API/postUpdateFieldValue.js'
import postUpdateStepStatus from '../functions/API/postUpdateStepStatus.js'
import postInitiateUpload from '../functions/API/postInitiateUpload.js'
import postUploadChunk from '../functions/API/postUploadChunk.js'
import postCompleteUpload from '../functions/API/postCompleteUpload.js'
import postInitiateStepPayment from '../functions/API/postInitiateStepPayment.js'
import { toast } from '../lib/toast.js'
import {
  ArrowLeft, Upload, FileText, CheckCircle2, AlertCircle, CreditCard, X, Info, ExternalLink,
} from 'lucide-react'

// --- Helpers (matching mobile app logic) ---

function getExistingValue(field) {
  switch (field.field_type_snapshot) {
    case 'BOOLEAN': return field.value_boolean
    case 'DATE': return field.value_date || null
    case 'NUMBER':
    case 'CURRENCY': return field.value_number != null ? String(field.value_number) : ''
    case 'FILE': return field.value_file_url || ''
    case 'DROPDOWN':
      if (field.value_json) {
        try { return typeof field.value_json === 'string' ? JSON.parse(field.value_json) : field.value_json }
        catch { return null }
      }
      return null
    case 'MULTI_SELECT':
      if (field.value_json) {
        try {
          const v = typeof field.value_json === 'string' ? JSON.parse(field.value_json) : field.value_json
          return Array.isArray(v) ? v : []
        } catch { return [] }
      }
      return []
    default: return field.value_text || ''
  }
}

function getFieldOptions(field) {
  const rules = field.validation_rules_snapshot
  if (!rules) return []
  const parsed = typeof rules === 'string' ? JSON.parse(rules) : rules
  return parsed?.options || []
}

function getValidationRules(field) {
  const rules = field.validation_rules_snapshot
  if (!rules) return {}
  return typeof rules === 'string' ? JSON.parse(rules) : rules
}

function validateFieldRules(field, value) {
  const rules = getValidationRules(field)
  const type = field.field_type_snapshot
  const errors = []

  if (value === null || value === undefined || value === '') return errors

  if (type === 'TEXT' || type === 'TEXTAREA') {
    const str = String(value)
    if (rules.min_length && str.length < rules.min_length) errors.push(`Must be at least ${rules.min_length} characters`)
    if (rules.max_length && str.length > rules.max_length) errors.push(`Must be no more than ${rules.max_length} characters`)
    if (rules.pattern) {
      try {
        if (!new RegExp(rules.pattern).test(str)) errors.push(rules.pattern_message || 'Invalid format')
      } catch { /* ignore invalid regex */ }
    }
  }

  if (type === 'NUMBER' || type === 'CURRENCY') {
    const num = parseFloat(value)
    if (isNaN(num)) {
      errors.push('Must be a valid number')
    } else {
      if (rules.min_value != null && num < rules.min_value) errors.push(`Must be at least ${rules.min_value}`)
      if (rules.max_value != null && num > rules.max_value) errors.push(`Must be no more than ${rules.max_value}`)
      if (type === 'NUMBER' && rules.allow_decimals === false && !Number.isInteger(num)) errors.push('Decimals are not allowed')
    }
  }

  if (type === 'DATE' && value) {
    const d = new Date(value)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    if (rules.allow_past === false && d < today) errors.push('Past dates are not allowed')
    if (rules.allow_future === false && d > today) errors.push('Future dates are not allowed')
    if (rules.min_date && d < new Date(rules.min_date)) errors.push(`Date must be on or after ${new Date(rules.min_date).toLocaleDateString('en-GB')}`)
    if (rules.max_date && d > new Date(rules.max_date)) errors.push(`Date must be on or before ${new Date(rules.max_date).toLocaleDateString('en-GB')}`)
  }

  if (type === 'MULTI_SELECT' && Array.isArray(value)) {
    if (rules.min_selections && value.length < rules.min_selections) errors.push(`Select at least ${rules.min_selections}`)
    if (rules.max_selections && value.length > rules.max_selections) errors.push(`Select no more than ${rules.max_selections}`)
  }

  return errors
}

const CHUNK_SIZE = 4 * 1024 * 1024 // 4MB

export default function OnboardingStepDetail() {
  const { stepId } = useParams()
  const navigate = useNavigate()
  const { settings } = useContext(GlobalContext)
  const primaryColour = settings?.primary_hex_colour || '#1F2937'

  const [loading, setLoading] = useState(true)
  const [instance, setInstance] = useState(null)
  const [step, setStep] = useState(null)
  const [fieldValues, setFieldValues] = useState({})
  const [submitting, setSubmitting] = useState(false)

  // Document upload state
  const [uploadFiles, setUploadFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const fileInputRef = useRef(null)
  const [dragOver, setDragOver] = useState(false)

  // Payment state
  const [paymentLoading, setPaymentLoading] = useState(false)

  useEffect(() => {
    loadStep()
  }, [stepId])

  async function loadStep() {
    // We need to find which instance has this step - fetch all instances first
    try {
      const { default: getTenantTaskInstances } = await import('../functions/API/getTenantTaskInstances.js')
      const instResponse = await getTenantTaskInstances()
      const instances = instResponse.data?.task_instances || []
      if (!instances.length) {
        navigate('/onboarding')
        return
      }

      const inst = instances[0]
      const detailResponse = await getTenantTaskInstance(inst.id)
      if (!detailResponse.response?.ok || !detailResponse.data?.task_instance) {
        toast.error('Failed to load step')
        navigate('/onboarding')
        return
      }

      const fullInstance = detailResponse.data.task_instance
      setInstance(fullInstance)

      const foundStep = fullInstance.steps?.find(s => String(s.id) === String(stepId))
      if (!foundStep) {
        toast.error('Step not found')
        navigate('/onboarding')
        return
      }

      setStep(foundStep)

      // Initialize field values
      const initial = {}
      ;(foundStep.fields || []).forEach(field => {
        initial[field.id] = getExistingValue(field)
      })
      setFieldValues(initial)

      // Load existing uploads for document steps
      if (foundStep.step_type_snapshot === 'document_upload' && foundStep.step_result?.files) {
        setUploadFiles(foundStep.step_result.files.map(f => ({
          name: f.original_name || f.name,
          size: f.size,
          status: 'complete',
          url: f.url,
        })))
      }

      setLoading(false)
    } catch (e) {
      toast.error('Failed to load step')
      navigate('/onboarding')
    }
  }

  function updateLocalValue(fieldId, value) {
    setFieldValues(prev => ({ ...prev, [fieldId]: value }))
  }

  // --- Standard Step: Save ---

  async function handleSaveStandard() {
    const sortedFields = [...(step.fields || [])]
      .sort((a, b) => (a.position_snapshot || 0) - (b.position_snapshot || 0))
      .filter(f => f.field_type_snapshot !== 'FILE')

    // Validate required
    const missingFields = sortedFields.filter(f => {
      if (!f.is_required_snapshot) return false
      const val = fieldValues[f.id]
      if (f.field_type_snapshot === 'BOOLEAN') return val !== true && val !== false
      if (val === null || val === undefined || val === '') return true
      if (Array.isArray(val) && val.length === 0) return true
      return false
    })

    if (missingFields.length > 0) {
      const names = missingFields.map(f => f.name_snapshot).join(', ')
      toast.error(`Please complete: ${names}`)
      return
    }

    // Validate field rules
    for (const field of sortedFields) {
      const errors = validateFieldRules(field, fieldValues[field.id])
      if (errors.length > 0) {
        toast.error(`${field.name_snapshot}: ${errors[0]}`)
        return
      }
    }

    setSubmitting(true)

    // Save all field values in parallel
    const savePromises = sortedFields.map(f => {
      const val = fieldValues[f.id]
      if (val === undefined) return Promise.resolve()
      return postUpdateFieldValue({ id: f.id, value: val })
    })
    await Promise.all(savePromises)

    // Submit step as completed
    const response = await postUpdateStepStatus({ id: step.id, status: 'COMPLETED' })
    if (response.response?.ok) {
      toast.success('Step completed')
      navigate('/onboarding')
    } else {
      toast.error(response.data?.message || 'Failed to submit step')
    }

    setSubmitting(false)
  }

  // --- Document Upload ---

  async function handleFileSelect(files) {
    if (!files?.length) return
    const config = step.step_config_snapshot || {}
    const allowedTypes = config.allowed_types || []
    const maxSizeMb = config.max_size_mb || 50

    for (const file of files) {
      // Check file type
      if (allowedTypes.length > 0) {
        const ext = '.' + file.name.split('.').pop().toLowerCase()
        if (!allowedTypes.some(t => t.toLowerCase() === ext)) {
          toast.error(`File type ${ext} is not allowed. Allowed: ${allowedTypes.join(', ')}`)
          continue
        }
      }

      // Check file size
      if (file.size > maxSizeMb * 1024 * 1024) {
        toast.error(`${file.name} exceeds the ${maxSizeMb}MB limit`)
        continue
      }

      // Add to list and upload
      const fileEntry = { name: file.name, size: file.size, status: 'uploading', progress: 0 }
      setUploadFiles(prev => [...prev, fileEntry])
      await uploadFile(file)
    }
  }

  async function uploadFile(file) {
    setUploading(true)
    setUploadProgress(0)

    try {
      // Initiate
      const initResponse = await postInitiateUpload({
        task_instance_step_id: step.id,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type,
      })

      if (!initResponse.response?.ok) {
        toast.error(initResponse.data?.message || 'Failed to start upload')
        setUploadFiles(prev => prev.map(f =>
          f.name === file.name && f.status === 'uploading' ? { ...f, status: 'error' } : f
        ))
        setUploading(false)
        return
      }

      const uploadId = initResponse.data.upload_id

      // Upload chunks
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE)
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE
        const end = Math.min(start + CHUNK_SIZE, file.size)
        const chunk = file.slice(start, end)

        const base64 = await readFileAsBase64(chunk)

        const chunkResponse = await postUploadChunk({
          upload_id: uploadId,
          chunk_index: i,
          chunk_data: base64,
        })

        if (!chunkResponse.response?.ok) {
          toast.error('Upload failed')
          setUploadFiles(prev => prev.map(f =>
            f.name === file.name && f.status === 'uploading' ? { ...f, status: 'error' } : f
          ))
          setUploading(false)
          return
        }

        const progress = Math.round(((i + 1) / totalChunks) * 100)
        setUploadProgress(progress)
        setUploadFiles(prev => prev.map(f =>
          f.name === file.name && f.status === 'uploading' ? { ...f, progress } : f
        ))
      }

      // Complete
      const completeResponse = await postCompleteUpload({
        upload_id: uploadId,
      })

      if (completeResponse.response?.ok) {
        setUploadFiles(prev => prev.map(f =>
          f.name === file.name && f.status === 'uploading' ? { ...f, status: 'complete', progress: 100 } : f
        ))
        toast.success(`${file.name} uploaded`)
      } else {
        toast.error('Failed to complete upload')
        setUploadFiles(prev => prev.map(f =>
          f.name === file.name && f.status === 'uploading' ? { ...f, status: 'error' } : f
        ))
      }
    } catch (e) {
      toast.error('Upload failed')
      setUploadFiles(prev => prev.map(f =>
        f.name === file.name && f.status === 'uploading' ? { ...f, status: 'error' } : f
      ))
    }

    setUploading(false)
    setUploadProgress(0)
  }

  function readFileAsBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]
        resolve(base64)
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  async function handleCompleteDocUpload() {
    const completedUploads = uploadFiles.filter(f => f.status === 'complete')
    if (completedUploads.length === 0) {
      toast.error('Please upload at least one file')
      return
    }

    setSubmitting(true)
    const response = await postUpdateStepStatus({ id: step.id, status: 'COMPLETED' })
    if (response.response?.ok) {
      toast.success('Step completed')
      navigate('/onboarding')
    } else {
      toast.error(response.data?.message || 'Failed to complete step')
    }
    setSubmitting(false)
  }

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    setDragOver(false)
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setDragOver(false)
    handleFileSelect(e.dataTransfer.files)
  }, [step])

  // --- Payment Step ---

  async function handleInitiatePayment() {
    setPaymentLoading(true)
    const response = await postInitiateStepPayment({ task_instance_step_id: step.id })

    if (!response.response?.ok) {
      toast.error(response.data?.message || 'Failed to initiate payment')
      setPaymentLoading(false)
      return
    }

    const { token, client_code, gateway_url, amount } = response.data
    const returnUrl = `${window.location.origin}/onboarding/payment-result?step_id=${step.id}`

    // Redirect to AllPay - build form and submit
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = gateway_url

    const fields = {
      token,
      clientCode: client_code,
      customerReference: instance?.id?.toString() || '',
      amount: 0,
      paymentReason: response.data.description || step.name_snapshot,
      returnUrl,
    }

    for (const [key, value] of Object.entries(fields)) {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = key
      input.value = value
      form.appendChild(input)
    }

    document.body.appendChild(form)
    form.submit()
  }

  // --- Info Step ---

  async function handleCompleteInfo() {
    setSubmitting(true)
    const response = await postUpdateStepStatus({ id: step.id, status: 'COMPLETED' })
    if (response.response?.ok) {
      toast.success('Step completed')
      navigate('/onboarding')
    } else {
      toast.error(response.data?.message || 'Failed to complete step')
    }
    setSubmitting(false)
  }

  // --- Multi-select toggle ---

  function handleMultiToggle(fieldId, optionValue) {
    const current = fieldValues[fieldId] || []
    const updated = current.includes(optionValue)
      ? current.filter(v => v !== optionValue)
      : [...current, optionValue]
    updateLocalValue(fieldId, updated)
  }

  // --- Render ---

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: primaryColour }}></div>
      </div>
    )
  }

  if (!step) return null

  const sortedFields = step.step_type_snapshot === 'standard'
    ? [...(step.fields || [])]
        .sort((a, b) => (a.position_snapshot || 0) - (b.position_snapshot || 0))
        .filter(f => f.field_type_snapshot !== 'FILE')
    : []

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: primaryColour }}>
      {/* Header */}
      <div>
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center">
          <button
            onClick={() => navigate('/onboarding')}
            className="flex items-center gap-1.5 text-sm text-white/70 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <h1 className="flex-1 text-center text-base font-semibold text-white pr-12">
            {step.name_snapshot}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-white rounded-t-[24px] shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        <div className="max-w-2xl mx-auto px-4 pt-6 pb-6">

          {/* ===== STANDARD STEP ===== */}
          {step.step_type_snapshot === 'standard' && (
            <>
              {step.description_snapshot && (
                <p className="text-sm text-gray-500 text-center mb-6">
                  {step.description_snapshot}
                </p>
              )}

              <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200 p-6">
                <div className="space-y-5">
                  {sortedFields.map(field => {
                    const type = field.field_type_snapshot
                    const required = field.is_required_snapshot
                    const label = field.name_snapshot + (required ? ' *' : '')
                    const placeholder = field.placeholder_snapshot || ''
                    const helpText = field.help_text_snapshot
                    const helpUrl = field.help_url_snapshot
                    const rules = getValidationRules(field)

                    return (
                      <div key={field.id}>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          {label}
                        </label>

                        {type === 'TEXT' && (
                          <input
                            type="text"
                            value={fieldValues[field.id] || ''}
                            onChange={(e) => updateLocalValue(field.id, e.target.value)}
                            placeholder={placeholder || 'Enter text'}
                            maxLength={rules.max_length || undefined}
                            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent"
                          />
                        )}

                        {type === 'TEXTAREA' && (
                          <textarea
                            value={fieldValues[field.id] || ''}
                            onChange={(e) => updateLocalValue(field.id, e.target.value)}
                            placeholder={placeholder || 'Enter details'}
                            maxLength={rules.max_length || undefined}
                            rows={4}
                            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent resize-none"
                          />
                        )}

                        {type === 'NUMBER' && (
                          <input
                            type="number"
                            value={fieldValues[field.id] != null ? String(fieldValues[field.id]) : ''}
                            onChange={(e) => updateLocalValue(field.id, e.target.value)}
                            placeholder={placeholder || '0'}
                            step={rules.allow_decimals === false ? '1' : 'any'}
                            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent"
                          />
                        )}

                        {type === 'CURRENCY' && (
                          <div className="flex">
                            <span className="inline-flex items-center px-3.5 rounded-l-xl border border-r-0 border-gray-200 bg-gray-100 text-sm font-medium text-gray-600">
                              £
                            </span>
                            <input
                              type="number"
                              value={fieldValues[field.id] != null ? String(fieldValues[field.id]) : ''}
                              onChange={(e) => updateLocalValue(field.id, e.target.value)}
                              placeholder={placeholder || '0.00'}
                              step="0.01"
                              className="flex-1 rounded-r-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent"
                            />
                          </div>
                        )}

                        {type === 'DATE' && (
                          <input
                            type="date"
                            value={fieldValues[field.id] || ''}
                            onChange={(e) => updateLocalValue(field.id, e.target.value)}
                            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent"
                          />
                        )}

                        {type === 'BOOLEAN' && (
                          <div className="flex gap-3">
                            {[
                              { value: true, label: 'Yes' },
                              { value: false, label: 'No' },
                            ].map(opt => {
                              const isSelected = fieldValues[field.id] === opt.value
                              return (
                                <button
                                  key={String(opt.value)}
                                  onClick={() => updateLocalValue(field.id, opt.value)}
                                  className={`flex-1 rounded-xl border-2 px-4 py-3 text-sm font-medium transition-colors ${
                                    isSelected
                                      ? 'text-white'
                                      : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300'
                                  }`}
                                  style={isSelected ? { borderColor: primaryColour, backgroundColor: primaryColour } : undefined}
                                >
                                  {opt.label}
                                </button>
                              )
                            })}
                          </div>
                        )}

                        {type === 'DROPDOWN' && (() => {
                          const options = getFieldOptions(field)
                          return (
                            <div className="space-y-2">
                              {options.map(opt => {
                                const isSelected = fieldValues[field.id] === opt.value
                                return (
                                  <button
                                    key={opt.value}
                                    onClick={() => updateLocalValue(field.id, opt.value)}
                                    className={`w-full flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-sm text-left transition-colors ${
                                      isSelected
                                        ? 'bg-gray-50'
                                        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                                    }`}
                                    style={isSelected ? { borderColor: primaryColour } : undefined}
                                  >
                                    <div
                                      className="h-4 w-4 rounded-full border-2 flex items-center justify-center"
                                      style={{ borderColor: isSelected ? primaryColour : '#D1D5DB' }}
                                    >
                                      {isSelected && <div className="h-2 w-2 rounded-full" style={{ backgroundColor: primaryColour }} />}
                                    </div>
                                    <span className={isSelected ? 'font-medium text-gray-900' : 'text-gray-700'}>
                                      {opt.label}
                                    </span>
                                  </button>
                                )
                              })}
                            </div>
                          )
                        })()}

                        {type === 'MULTI_SELECT' && (() => {
                          const options = getFieldOptions(field)
                          const selected = fieldValues[field.id] || []
                          const maxSel = rules.max_selections
                          const atMax = maxSel && selected.length >= maxSel
                          return (
                            <div className="space-y-2">
                              {maxSel && (
                                <p className="text-xs text-gray-400 mb-1">Select up to {maxSel}</p>
                              )}
                              {options.map(opt => {
                                const isSelected = selected.includes(opt.value)
                                const disabled = atMax && !isSelected
                                return (
                                  <button
                                    key={opt.value}
                                    onClick={() => !disabled && handleMultiToggle(field.id, opt.value)}
                                    disabled={disabled}
                                    className={`w-full flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-sm text-left transition-colors ${
                                      isSelected
                                        ? 'bg-gray-50'
                                        : disabled
                                          ? 'border-gray-100 bg-gray-50 opacity-40'
                                          : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                                    }`}
                                    style={isSelected ? { borderColor: primaryColour } : undefined}
                                  >
                                    <div
                                      className="h-4 w-4 rounded border-2 flex items-center justify-center"
                                      style={isSelected ? { borderColor: primaryColour, backgroundColor: primaryColour } : { borderColor: '#D1D5DB' }}
                                    >
                                      {isSelected && (
                                        <svg className="h-3 w-3 text-white" viewBox="0 0 12 12" fill="none">
                                          <path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                      )}
                                    </div>
                                    <span className={isSelected ? 'font-medium text-gray-900' : 'text-gray-700'}>
                                      {opt.label}
                                    </span>
                                  </button>
                                )
                              })}
                            </div>
                          )
                        })()}

                        {(helpText || helpUrl) && (
                          <div className="flex items-center gap-2 mt-1">
                            {helpText && <p className="text-xs text-gray-400">{helpText}</p>}
                            {helpUrl && (
                              <a href={helpUrl} target="_blank" rel="noopener noreferrer" title="Help" className="text-gray-400 hover:text-gray-600">
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Save Button */}
              <div className="mt-6">
                <button
                  onClick={handleSaveStandard}
                  disabled={submitting}
                  className="w-full rounded-xl px-4 py-3.5 text-sm font-semibold text-white disabled:opacity-50"
                  style={{ backgroundColor: primaryColour }}
                >
                  {submitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </>
          )}

          {/* ===== DOCUMENT UPLOAD STEP ===== */}
          {step.step_type_snapshot === 'document_upload' && (
            <>
              {step.description_snapshot && (
                <p className="text-sm text-gray-500 text-center mb-6">
                  {step.description_snapshot}
                </p>
              )}
              {(step.help_text_snapshot || step.help_url_snapshot) && (
                <div className="flex items-center gap-2 justify-center mb-4">
                  {step.help_text_snapshot && <p className="text-xs text-gray-400">{step.help_text_snapshot}</p>}
                  {step.help_url_snapshot && (
                    <a href={step.help_url_snapshot} target="_blank" rel="noopener noreferrer" title="Help" className="text-gray-400 hover:text-gray-600">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              )}

              <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200 p-6">
                {/* Drop zone */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                    dragOver
                      ? 'bg-gray-50'
                      : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                  }`}
                  style={dragOver ? { borderColor: primaryColour } : undefined}
                >
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-700">
                    Click to upload or drag and drop
                  </p>
                  {(() => {
                    const config = step.step_config_snapshot || {}
                    const types = config.allowed_types?.join(', ') || 'Any file type'
                    const maxSize = config.max_size_mb || 50
                    return (
                      <p className="text-xs text-gray-400 mt-1">
                        {types} up to {maxSize}MB
                      </p>
                    )
                  })()}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={(e) => handleFileSelect(e.target.files)}
                    className="hidden"
                    accept={step.step_config_snapshot?.allowed_types?.join(',') || undefined}
                  />
                </div>

                {/* Upload progress */}
                {uploading && (
                  <div className="mt-4">
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%`, backgroundColor: primaryColour }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-center">{uploadProgress}%</p>
                  </div>
                )}

                {/* File list */}
                {uploadFiles.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {uploadFiles.map((file, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-3 rounded-xl bg-gray-50 px-4 py-3"
                      >
                        <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
                          <p className="text-xs text-gray-400">
                            {file.size ? `${(file.size / 1024 / 1024).toFixed(1)}MB` : ''}
                          </p>
                        </div>
                        {file.status === 'complete' && (
                          <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                        )}
                        {file.status === 'uploading' && (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400 flex-shrink-0"></div>
                        )}
                        {file.status === 'error' && (
                          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Complete Button */}
              <div className="mt-6">
                <button
                  onClick={handleCompleteDocUpload}
                  disabled={submitting || uploading || uploadFiles.filter(f => f.status === 'complete').length === 0}
                  className="w-full rounded-xl px-4 py-3.5 text-sm font-semibold text-white disabled:opacity-50"
                  style={{ backgroundColor: primaryColour }}
                >
                  {submitting ? 'Completing...' : 'Complete'}
                </button>
              </div>
            </>
          )}

          {/* ===== PAYMENT STEP ===== */}
          {step.step_type_snapshot === 'payment' && (
            <>
              <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200 p-8 text-center">
                <div
                  className="mx-auto flex h-16 w-16 items-center justify-center rounded-full mb-4"
                  style={{ backgroundColor: primaryColour + '12' }}
                >
                  <CreditCard className="h-8 w-8" style={{ color: primaryColour }} />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Payment Required</h2>
                {step.step_config_snapshot?.amount && (
                  <p className="text-3xl font-bold mt-2" style={{ color: primaryColour }}>
                    £{parseFloat(step.step_config_snapshot.amount).toFixed(2)}
                  </p>
                )}
                {step.description_snapshot && (
                  <p className="text-sm text-gray-500 mt-3">{step.description_snapshot}</p>
                )}
                {(step.help_text_snapshot || step.help_url_snapshot) && (
                  <div className="flex items-center gap-2 justify-center mt-2">
                    {step.help_text_snapshot && <p className="text-xs text-gray-400">{step.help_text_snapshot}</p>}
                    {step.help_url_snapshot && (
                      <a href={step.help_url_snapshot} target="_blank" rel="noopener noreferrer" title="Help" className="text-gray-400 hover:text-gray-600">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                )}

                <button
                  onClick={handleInitiatePayment}
                  disabled={paymentLoading}
                  className="mt-6 w-full rounded-xl px-4 py-3.5 text-sm font-semibold text-white disabled:opacity-50"
                  style={{ backgroundColor: primaryColour }}
                >
                  {paymentLoading ? 'Processing...' : 'Make Payment'}
                </button>
              </div>
            </>
          )}

          {/* ===== INFORMATION STEP ===== */}
          {step.step_type_snapshot === 'information' && (
            <>
              <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200 p-8">
                <div
                  className="mx-auto flex h-14 w-14 items-center justify-center rounded-full mb-4"
                  style={{ backgroundColor: primaryColour + '12' }}
                >
                  <Info className="h-7 w-7" style={{ color: primaryColour }} />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 text-center">{step.name_snapshot}</h2>
                {step.description_snapshot && (
                  <p className="text-sm text-gray-600 mt-3 whitespace-pre-wrap leading-relaxed">
                    {step.description_snapshot}
                  </p>
                )}
                {(step.help_text_snapshot || step.help_url_snapshot) && (
                  <div className="flex items-center gap-2 mt-3">
                    {step.help_text_snapshot && <p className="text-xs text-gray-400">{step.help_text_snapshot}</p>}
                    {step.help_url_snapshot && (
                      <a href={step.help_url_snapshot} target="_blank" rel="noopener noreferrer" title="Help" className="text-gray-400 hover:text-gray-600">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-6">
                <button
                  onClick={handleCompleteInfo}
                  disabled={submitting}
                  className="w-full rounded-xl px-4 py-3.5 text-sm font-semibold text-white disabled:opacity-50"
                  style={{ backgroundColor: primaryColour }}
                >
                  {submitting ? 'Completing...' : "I've read this"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
